import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Shield, Lock, Loader2, Check } from "lucide-react";
import { getPermissions, savePermissions } from "@/lib/quotationApi";

const ROLE_COLS = [
  { key: "staff", label: "Staff" },
  { key: "manager", label: "Manager" },
  { key: "admin", label: "Admin" },
];

// One capability row: label + a toggle per role (admin locked on).
function PermissionRow({ cap, isOn, onToggle }) {
  return (
    <div
      className="grid grid-cols-[1fr_repeat(3,64px)] sm:grid-cols-[1fr_repeat(3,88px)] gap-1 items-center px-3 py-2.5 rounded-lg hover:bg-white/[0.02] border-b border-white/5"
      data-testid={`perm-row-${cap.key}`}
    >
      <div className="text-sm text-white/80 pr-2">{cap.label}</div>
      {ROLE_COLS.map((r) => {
        const isAdmin = r.key === "admin";
        const on = isAdmin ? true : isOn(cap.key, r.key);
        return (
          <div key={r.key} className="flex justify-center">
            <button
              type="button"
              disabled={isAdmin}
              onClick={() => onToggle(cap.key, r.key)}
              data-testid={`perm-${cap.key}-${r.key}`}
              aria-pressed={on}
              className={`w-6 h-6 rounded-md border flex items-center justify-center transition ${
                on
                  ? "bg-[#C5A880] border-[#C5A880] text-black"
                  : "bg-transparent border-white/15 text-transparent hover:border-white/35"
              } ${isAdmin ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
            >
              {isAdmin ? <Lock size={12} className="text-black/70" /> : on ? <Check size={14} /> : null}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function PermissionsPanel() {
  const [meta, setMeta] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [locked, setLocked] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getPermissions();
        setMeta(data.capabilities || []);
        setMatrix(data.matrix || {});
        setLocked(data.locked || {});
      } catch {
        toast.error("Failed to load permissions");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const groups = useMemo(() => {
    const g = {};
    meta.forEach((c) => {
      (g[c.group] = g[c.group] || []).push(c);
    });
    return g;
  }, [meta]);

  const isOn = (cap, role) => (matrix[cap] || []).includes(role);

  const toggle = (cap, role) => {
    if (role === "admin") return; // admin always on, locked
    setMatrix((prev) => {
      const cur = new Set(prev[cap] || []);
      if (cur.has(role)) cur.delete(role);
      else cur.add(role);
      return { ...prev, [cap]: Array.from(cur) };
    });
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Only staff/manager are editable; admin is implicit.
      const payload = {};
      Object.keys(matrix).forEach((cap) => {
        payload[cap] = (matrix[cap] || []).filter((r) => r === "staff" || r === "manager");
      });
      const { data } = await savePermissions(payload);
      setMatrix(data.matrix || matrix);
      toast.success("Permissions saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-white/40" data-testid="permissions-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading permissions…
      </div>
    );
  }

  return (
    <div data-testid="permissions-panel">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-[#C5A880]/15 border border-[#C5A880]/30 flex items-center justify-center shrink-0">
          <Shield size={17} className="text-[#C5A880]" />
        </div>
        <div>
          <div className="font-display font-semibold text-[15px]">Access control</div>
          <p className="text-sm text-white/50 leading-snug max-w-xl">
            Choose what each role can do in this workspace. Admins always have full
            access. Changes apply the next time a member loads the app.
          </p>
        </div>
      </div>

      {/* Column header */}
      <div className="grid grid-cols-[1fr_repeat(3,64px)] sm:grid-cols-[1fr_repeat(3,88px)] gap-1 px-3 py-2 text-[10px] uppercase tracking-wider text-white/40 border-b border-white/5">
        <div>Capability</div>
        {ROLE_COLS.map((r) => (
          <div key={r.key} className="text-center">{r.label}</div>
        ))}
      </div>

      {Object.entries(groups).map(([group, caps]) => (
        <div key={group} className="mb-1">
          <div className="px-3 pt-4 pb-1.5 text-[11px] uppercase tracking-wider text-[#C5A880]/80 font-semibold">
            {group}
          </div>
          {caps.map((c) => (
            <PermissionRow key={c.key} cap={c} isOn={isOn} onToggle={toggle} />
          ))}
        </div>
      ))}

      {/* Locked, admin-only capabilities (informational) */}
      <div className="mt-5 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3">
        <div className="text-[11px] uppercase tracking-wider text-white/40 mb-2 flex items-center gap-1.5">
          <Lock size={11} /> Always admin-only
        </div>
        <div className="text-sm text-white/60 space-y-1">
          <div>Manage team members (invite / change role / remove)</div>
          <div>Rename or delete the workspace</div>
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="permissions-save-btn"
          className="btn-primary flex items-center gap-2 disabled:opacity-60"
        >
          {saving ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />}
          {saving ? "Saving…" : "Save permissions"}
        </button>
      </div>
    </div>
  );
}
